import React, { useEffect, useRef, useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { toast } from 'react-hot-toast';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { Formik, FormikProps } from 'formik';
import router from 'next/router';

import { withAuth } from '@/components/with_auth';
import {
    UserContent, initializedAtom, onboardingAtom, showAgendaOnboarding, userAtom,
} from '@/components/with_auth/State';
import Page from '@/components/layout/page';
import Button, { ButtonSize, ButtonType } from '@/components/commons/button';
import Title from '@/components/commons/page-elements/Title';
import { haveErrors, reportGeneralError } from '@/components/onboarding/Shared';
import routes from '@/services/routes';
import {
    handleValidationErrors, onCompletedHandler, onErrorHandler, handleFetchErrors,
} from '@/services/apollo';
import { UPDATE_USER, type UpdateUserAttributesType } from '@/components/settings/Actions';
import { FETCH_AGENDAS, DEFAULT_AGENDA } from '@/components/calendar/agendas/Actions';

import AgendasGrid from '@/components/calendar/agendas/AgendasGrid';
import VacationModeToggle from '@/components/calendar/agendas/VacationModeToggle';
import OnboardingCalendarsModal from '@/components/calendar/agendas/OnboardingCalendarsModal';
import Plus from '@/components/commons/icons/Plus';
import ThumbsUpOutline from '@/components/commons/icons/ThumbsUpOutline';
import { SortDirection } from '@/services/sorting';
import WarningCard from '@/components/commons/warning_card';

interface Props {
    // user gets injected from withAuth HOC
    readonly user: UserContent
    readonly setUserRecoil: (user: UserContent) => void;
}

function Agendas({ user, setUserRecoil }: Props) {
    const [agendas, setAgendas] = useState(null);
    const [isError, setIsError] = useState(false);
    const [currentVacationMode, setVacationMode] = useState(false);
    const [performRefetch, setPerformRefetch] = useState(false);
    const [formError, setFormError] = useState('');
    const [onboardingData, setOnboardingData] = useRecoilState(onboardingAtom);
    const isAgendaOnboarding = useRecoilValue(showAgendaOnboarding);

    const [updateVacationMode] = useMutation(UPDATE_USER);
    const [updateUser] = useMutation(UPDATE_USER);
    const [defaultAgenda] = useMutation(DEFAULT_AGENDA);

    const agendaStatusformRef = useRef<FormikProps<any>>(null);

    const setters = {
        setUser: useSetRecoilState(userAtom),
        setInitialized: useSetRecoilState(initializedAtom),
    };

    const {
        loading, refetch,
    } = useQuery(FETCH_AGENDAS, {
        variables: {
            id: null,
            sortColumn: 'name',
            sortDirection: SortDirection.ASC,
        },
        notifyOnNetworkStatusChange: true,
        fetchPolicy: 'no-cache',
        onError: onErrorHandler(setIsError),
        onCompleted: onCompletedHandler(setIsError, (data) => {
            const agendasData = data.user.agendaMails.map((agendaItem) => {
                const agenda = { ...agendaItem };
                agenda.option = agendaItem.id;
                agenda.editAction = agendaItem.id;
                agenda.deleteAction = agendaItem.id;
                agenda.agendaRules = agenda.agendaRules.map((rule) => {
                    const ruleItem = { ...rule };
                    ruleItem.calendars = rule.calendar.map((instance) => ({
                        value: instance.calendar.id,
                        label: instance.effectiveLabel,
                    }));
                    delete ruleItem.calendar;
                    delete ruleItem.__typename;
                    return ruleItem;
                });
                delete agenda.__typename;
                return agenda;
            });
            setAgendas(agendasData);
            setFormError('');
        }),
    });

    const handleAgendaStatus = () => {
        if (agendaStatusformRef.current) {
            agendaStatusformRef.current.handleSubmit();
        }
    };

    const handleDefaultAgendas = async () => {
        try {
            const response = await defaultAgenda({
                variables: { setDefaultAgendasInput: {} },
            });
            await refetch();
            if (response.data.setDefaultAgendas.setSuccessfully) {
                toast.success('Default Agendas Created Successfully.');
            } else {
                toast.success('Default Agendas Already Exist.');
            }
        } catch (err) {
            handleFetchErrors(err);
            setIsError(true);
            setFormError('An unexpected error occurred.  Please try again later.');
        }
    };

    useEffect(() => {
        if (performRefetch) setPerformRefetch(false);
    }, [performRefetch]);

    useEffect(() => {
        if (user) {
            setVacationMode(user.vacationMode);
        }
    }, [user]);

    useEffect(() => {
        // switch us to agenda onboarding
        if (onboardingData.type !== 'agenda') {
            setOnboardingData({
                ...onboardingData,
                type: 'agenda',
            });
        }
        // advance to the next onboarding step if the user already
        // reviewed their calendars
        if (onboardingData.agenda === 1 && user.hasReviewedCalendars) {
            setOnboardingData({
                ...onboardingData,
                agenda: 2,
            });
        }
    });

    const handleUpdateVacationMode = async () => {
        const attributes = {
            vacationMode: !currentVacationMode,
        } as Partial<UpdateUserAttributesType>;

        try {
            const response = await updateVacationMode({
                variables: {
                    updateUserInput: {
                        attributes,
                    },
                },
            });
            if (response.data.updateUser.user) {
                const { user: newUser } = response.data.updateUser;
                setVacationMode(newUser.vacationMode);
                // make sure the recoil state is updated so agenda edit has
                // correct vacation mode when navigating
                setUserRecoil(newUser);
            } else if (response.data.updateUser.errors) {
                // TODO: Technically we _could_ get errors for other fields, which would lead to
                // TODO: confusion here.  Of course, that indicates a deeper problem of user objects
                // TODO: coming out of the DB in a not-valid state...
                const errors = response.data.updateUser.errors.map((error) => error.message).join(', ');
                setFormError(errors);
            } else {
                setFormError('An unknown error occurred.  Please try again later.');
            }
        } catch (_error) {
            setFormError('An unknown error occurred.  Please try again later.');
        }
    };

    return (
        <Page>
            <div className="flex flex-wrap fullHeightLessNavPage bg-lightGray bg-opacity-30 items-start">
                <div className="w-full p-4 sm:p-10">
                    <div className="flex relative pb-10 text-left items-center">
                        <Title
                            title="Agendas"
                        >
                            <div className="flex flex-wrap gap-2 justify-between items-center w-full">
                                <p>Create and modify the agendas that Sixty sends you.</p>
                                {
                                    isAgendaOnboarding
                                        ? (
                                            <Formik
                                                innerRef={agendaStatusformRef}
                                                onSubmit={(_frm, { setStatus, setSubmitting }) => {
                                                    const attributes = {
                                                        hasCreatedAgendas: true,
                                                    };

                                                    updateUser({
                                                        variables: {
                                                            updateUserInput: {
                                                                attributes,
                                                            },
                                                        },
                                                        notifyOnNetworkStatusChange: true,
                                                        fetchPolicy: 'no-cache',
                                                        onError: onErrorHandler((error) => {
                                                            setSubmitting(false);
                                                            reportGeneralError(error);
                                                        }),
                                                        onCompleted: onCompletedHandler(
                                                            () => { },
                                                            (data) => {
                                                                setSubmitting(false);
                                                                const { errors } = data.updateUser;
                                                                if (haveErrors(errors)) {
                                                                    handleValidationErrors(errors, setStatus, 'user');
                                                                    return;
                                                                }

                                                                setters.setUser(
                                                                    data.updateUser.user,
                                                                );
                                                                setters.setInitialized(true);
                                                                setStatus({ errors: {} });

                                                                router.push(routes.MY_SIXTY);
                                                            },
                                                        ),
                                                    });
                                                }}
                                                initialValues={{
                                                    hasCreatedAgendas: user?.hasCreatedAgendas,
                                                }}
                                            >
                                                <Button
                                                    size={ButtonSize.base}
                                                    icon={<ThumbsUpOutline className="w-3 h-3 m-auto mr-1" />}
                                                    text="I'm done"
                                                    type={ButtonType.submit}
                                                    onClick={handleAgendaStatus}
                                                />
                                            </Formik>
                                        )
                                        : (
                                            <div className="flex gap-3 flex-nowrap mt-4 sm:mt-0 w-full sm:w-auto">
                                                <Button
                                                    title="Add default set"
                                                    // externalStyles="flex-nowrap"
                                                    size={ButtonSize.base}
                                                    icon={(<Plus className="inline w-3 h-3 m-auto" />)}
                                                    type={ButtonType.secondary}
                                                    text="Add default set"
                                                    onClick={handleDefaultAgendas}
                                                />
                                                <Button
                                                    title="Add new agenda"
                                                    size={ButtonSize.base}
                                                    icon={(<Plus className="inline w-3 h-3 m-auto" />)}
                                                    text="Add new agenda"
                                                    type={ButtonType.submit}
                                                    onClick={() => router.push(routes.ADD_AGENDA)}
                                                />
                                            </div>
                                        )
                                }
                            </div>
                        </Title>
                    </div>
                    <div className="mb-[15px]">
                        <VacationModeToggle
                            isLoading={!user}
                            // withAuth should catch user fetch errors and redirect
                            isError={false}
                            onChange={handleUpdateVacationMode}
                            vacationMode={currentVacationMode}
                        />
                    </div>

                    {
                        formError !== '' && (
                            <div className="w-full my-[20px]">
                                <WarningCard message={formError} />
                            </div>
                        )
                    }

                    <AgendasGrid
                        isError={isError}
                        loading={loading}
                        agendas={agendas}
                        vacationMode={currentVacationMode}
                        refetch={refetch}
                    />
                </div>
            </div>
            {isAgendaOnboarding
                && onboardingData.agenda === 1
                && !user.hasReviewedCalendars
                && <OnboardingCalendarsModal />}
        </Page>
    );
}

export default withAuth(Agendas);
